import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { FileUploader } from '@/components/forms/FileUploader';
import { WheelLogoPicker } from './WheelLogoPicker';
import type { StockClassification } from '@/lib/api/types';
import { useT } from '@/lib/i18n/useT';

/**
 * AddXotskiDialog — creates a new `xk-stock` row.
 *
 * Fields mirror the backend entity (`ox-platform/src/apps/xotski/entities/xk-stock.entity.ts`):
 *   name · description · translate (ES) · image · logo · unitCost ·
 *   initialQuantity · classification · rating (1-5) · restockThreshold
 *
 * `companyId` is injected by the caller (StockListPage) from the auth context.
 */

export interface NewXotski {
  name: string;
  description?: string;
  translate?: string;
  image?: string;
  logo?: string;
  unitCost?: number;
  initialQuantity?: number;
  classification?: StockClassification;
  rating: number;
  restockThreshold?: number;
}

export interface AddXotskiDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSave: (xotski: NewXotski) => Promise<void> | void;
  loading?: boolean;
  /** When provided the dialog acts as an editor seeded with these values. */
  initial?: Partial<NewXotski> | null;
}

/*
 * Classification UI is hidden for now. When re-enabling, restore the JSX
 * block below + the constants here:
 *
 *   const CLASSIFICATIONS: StockClassification[] = ['SEE', 'ENGAGE', 'TRY', 'ACT'];
 *   const CLASSIFICATION_HINT_KEY: Record<StockClassification, string> = {
 *     SEE: 'stock.dialog.classHint.SEE',
 *     ENGAGE: 'stock.dialog.classHint.ENGAGE',
 *     TRY: 'stock.dialog.classHint.TRY',
 *     ACT: 'stock.dialog.classHint.ACT',
 *   };
 */

const RARITY_COLORS = ['#C6D93B', '#6C1CD3', '#E2DFF2', '#B262F5', '#B262F5'] as const;

function rarityColorFor(r: number): string {
  const i = Math.max(1, Math.min(5, Math.round(r))) - 1;
  return RARITY_COLORS[i];
}

// Note: `unitCost` is hidden in the UI for now. It still exists on the
// underlying entity (`xk_stock.unit_cost`) — the dialog just never
// surfaces it; new rows are saved with `unitCost: undefined` so the
// column stays null until we choose to re-expose the field.
interface FormState {
  name: string;
  description: string;
  translate: string;
  image: string;
  logo: string;
  initialQuantity: number;
  classification: StockClassification | '';
  rating: number;
  restockThreshold: number;
}

const EMPTY: FormState = {
  name: '',
  description: '',
  translate: '',
  image: '',
  logo: '',
  initialQuantity: 100,
  classification: '',
  rating: 3,
  restockThreshold: 0,
};

function seedForm(initial: Partial<NewXotski> | null | undefined): FormState {
  if (!initial) return EMPTY;
  return {
    name: initial.name ?? '',
    description: initial.description ?? '',
    translate: initial.translate ?? '',
    image: initial.image ?? '',
    logo: initial.logo ?? '',
    initialQuantity: initial.initialQuantity ?? 0,
    classification: initial.classification ?? '',
    rating: initial.rating ?? 3,
    restockThreshold: initial.restockThreshold ?? 0,
  };
}

export function AddXotskiDialog({
  open,
  onOpenChange,
  onSave,
  loading,
  initial,
}: AddXotskiDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="scrollbar-thin max-h-[90vh] max-w-[640px] overflow-y-auto p-0">
        {open && (
          <XotskiFormBody
            initial={initial}
            onSave={onSave}
            onCancel={() => onOpenChange(false)}
            loading={loading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The xotski create/edit form body, decoupled from any modal shell so it
 * renders both inside AddXotskiDialog and inside the right-panel form host
 * (see FormPanelHost / XotskiForm). The surrounding shell owns open/close.
 */
export function XotskiFormBody({
  initial,
  onSave,
  onCancel,
  loading,
}: {
  initial?: Partial<NewXotski> | null;
  onSave: (xotski: NewXotski) => Promise<void> | void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<FormState>(() => seedForm(initial));
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(seedForm(initial));
    setUploading(false);
  }, [initial]);

  const [attempted, setAttempted] = useState(false);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  /**
   * Form errors keyed by field. Only surfaced after the user has tried to
   * submit once, so the dialog isn't yelling validation errors on first
   * render.
   */
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (form.name.trim().length < 2) {
    errors.name = t('stock.dialog.error.nameLen');
  }
  if (form.initialQuantity <= 0) {
    errors.initialQuantity = t('stock.dialog.error.qtyGt0');
  }
  if (!Number.isInteger(form.initialQuantity)) {
    errors.initialQuantity = t('stock.dialog.error.qtyInt');
  }
  // Unit cost validation removed alongside its hidden field.
  if (form.restockThreshold < 0) {
    errors.restockThreshold = t('stock.dialog.error.threshNeg');
  }
  if (form.restockThreshold > form.initialQuantity) {
    errors.restockThreshold = t('stock.dialog.error.threshExceeds');
  }
  if (form.rating < 1 || form.rating > 5) {
    errors.rating = t('stock.dialog.error.ratingRange');
  }

  const valid = Object.keys(errors).length === 0 && !loading && !uploading;

  const submit = () => {
    setAttempted(true);
    if (!valid) return;
    onSave({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      translate: form.translate.trim() || undefined,
      image: form.image.trim() || undefined,
      logo: form.logo.trim() || undefined,
      // Unit cost intentionally omitted — the UI doesn't collect it; the
      // column stays null. Re-add `form.unitCost` here when re-enabling.
      unitCost: undefined,
      initialQuantity: form.initialQuantity,
      // Classification is hidden in the UI for now — every new xotski
      // gets the 'SEE' default so the column has a value. Re-expose the
      // section in the JSX below to let users pick again.
      classification: form.classification || 'SEE',
      rating: form.rating,
      restockThreshold:
        form.restockThreshold > 0 ? form.restockThreshold : undefined,
    });
  };

  return (
        <div className="p-8">
          {/* ── Header ─────────────────────────────────────────── */}
          <header className="mb-6">
            <div
              className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.10em] text-[var(--color-xk-accent)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {isEdit ? t('stock.dialog.editingTitle') : t('stock.dialog.addingTitle')}
            </div>
            <h2
              className="mb-1.5 text-[22px] font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {isEdit
                ? `${t('stock.dialog.updateHeading')} ${initial?.name?.trim() || t('stock.dialog.updateHeadingFallback')}`
                : t('stock.dialog.newHeading')}
            </h2>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {isEdit
                ? t('stock.dialog.editLead')
                : t('stock.dialog.newLead')}
            </p>
          </header>

          {/* ── Identity ───────────────────────────────────────── */}
          <SectionLabel>{t('stock.dialog.section.identity')}</SectionLabel>

          <FieldLabel required>{t('stock.dialog.field.name')}</FieldLabel>
          <input
            type="text"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={t('stock.dialog.field.namePlaceholder')}
            autoFocus
            className={inputCls}
          />
          {attempted && errors.name && (
            <p className="mt-1 text-xs text-[var(--color-error)]">{errors.name}</p>
          )}
          <div className="h-4" />

          <FieldLabel>{t('stock.dialog.field.translate')}</FieldLabel>
          <input
            type="text"
            value={form.translate}
            onChange={(e) => set('translate', e.target.value)}
            placeholder={t('stock.dialog.field.translatePlaceholder')}
            className={inputCls}
          />
          <div className="h-4" />

          <FieldLabel>{t('stock.dialog.field.description')}</FieldLabel>
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={t('stock.dialog.field.descriptionPlaceholder')}
            rows={2}
            className={`${inputCls} min-h-[72px] resize-y`}
          />

          {/* ── Visuals ────────────────────────────────────────── */}
          <SectionLabel className="mt-6">{t('stock.dialog.section.visuals')}</SectionLabel>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>{t('stock.dialog.field.productImage')}</FieldLabel>
              <FileUploader
                value={form.image || null}
                onChange={(v) => set('image', v ?? '')}
                onUploadingChange={setUploading}
                preset="photo"
              />
            </div>
            <div>
              <FieldLabel>{t('stock.dialog.field.wheelLogo')}</FieldLabel>
              {/* Free-form upload + curated library picker. Ops drop
                  new logos into ox-platform/uploads/wheel-logos/ and they
                  appear automatically — no rebuild needed. */}
              <WheelLogoPicker
                value={form.logo || null}
                onChange={(v) => set('logo', v ?? '')}
                onUploadingChange={setUploading}
              />
            </div>
          </div>

          {/* ── Inventory ──────────────────────────────────────── */}
          <SectionLabel className="mt-6">{t('stock.dialog.section.inventory')}</SectionLabel>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel required>{t('stock.dialog.field.initialQty')}</FieldLabel>
              <input
                type="number"
                min={1}
                step={1}
                value={form.initialQuantity}
                onChange={(e) =>
                  set('initialQuantity', Math.max(0, parseInt(e.target.value || '0', 10)))
                }
                className={inputCls}
              />
              {attempted && errors.initialQuantity && (
                <p className="mt-1 text-xs text-[var(--color-error)]">
                  {errors.initialQuantity}
                </p>
              )}
            </div>
            {/* Unit cost field hidden for now — saved as null. */}
            <div>
              <FieldLabel>{t('stock.dialog.field.restockThreshold')}</FieldLabel>
              <input
                type="number"
                min={0}
                step={1}
                value={form.restockThreshold}
                onChange={(e) =>
                  set('restockThreshold', Math.max(0, parseInt(e.target.value || '0', 10)))
                }
                placeholder={t('stock.dialog.field.restockPlaceholder')}
                className={inputCls}
              />
              {attempted && errors.restockThreshold && (
                <p className="mt-1 text-xs text-[var(--color-error)]">
                  {errors.restockThreshold}
                </p>
              )}
            </div>
          </div>

          {/* Classification section is hidden for now — every xotski gets
              'SEE' by default in `submit()` above. To re-expose, restore
              the JSX block and remove the default in submit(). */}

          {/* ── Rarity ─────────────────────────────────────────── */}
          <SectionLabel className="mt-6">{t('stock.dialog.section.rarity')}</SectionLabel>
          <FieldLabel>{t('stock.dialog.field.rarityTier')}</FieldLabel>
          <div className="mb-2 flex items-center gap-4 rounded-lg border border-[var(--color-xk-border)] bg-[rgb(255_255_255/0.03)] p-3.5">
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((r) => {
                const sel = form.rating === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => set('rating', r)}
                    className={`h-8 w-8 rounded-md text-sm font-extrabold transition-colors ${
                      sel
                        ? 'border-2 border-[var(--color-xk-accent)] bg-[var(--color-accent-12)] text-[var(--color-xk-lavender)]'
                        : 'border border-[var(--color-xk-border)] text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]'
                    }`}
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
            <RarityBadge rating={form.rating} />
            <RarityBar rating={form.rating} />
          </div>
          <p className="mb-6 text-[11px] text-[var(--color-xk-text-muted)]">
            {t('stock.dialog.weight.weightOn')}{' '}
            <span className="font-bold text-[var(--color-xk-lavender)]">
              {Math.pow(6 - form.rating, 2)}
            </span>{' '}
            · {t('stock.dialog.weight.hint')}
          </p>

          {/* ── Validation summary (after first submit attempt) ──── */}
          {attempted && Object.keys(errors).length > 0 && (
            <div className="mt-5 rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-3 text-xs">
              <p className="mb-1 font-bold text-[var(--color-error)]">
                {Object.keys(errors).length === 1
                  ? t('stock.dialog.fixFieldsOne')
                  : t('stock.dialog.fixFieldsMany').replace(
                      '{count}',
                      String(Object.keys(errors).length),
                    )}
              </p>
              <ul className="list-disc pl-5 text-[var(--color-fg-primary)]">
                {Object.entries(errors).map(([k, msg]) => (
                  <li key={k}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="mt-5 flex justify-end gap-3 border-t border-[var(--color-xk-border)] pt-5">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[var(--color-xk-border)] px-5 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
            >
              {t('stock.dialog.cancel')}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading || uploading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2.5 text-[13px] font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading
                ? t('stock.dialog.saving')
                : isEdit
                  ? t('stock.dialog.saveChanges')
                  : t('stock.dialog.addToInventory')}
            </button>
          </div>
        </div>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────

const inputCls =
  'block w-full rounded-lg border border-[var(--color-xk-border)] bg-[rgb(255_255_255/0.05)] px-3 py-2.5 text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none';

function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`mb-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-xk-accent)] ${className ?? ''}`}
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {children}
    </div>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {children}
      {required && <span className="ml-1 text-[var(--color-xk-accent)]">*</span>}
    </label>
  );
}

/*
 * `ClassificationPill` removed alongside the hidden Classification UI.
 * Re-add it here when the section is re-enabled.
 */

function RarityBadge({ rating }: { rating: number }) {
  const t = useT();
  const tier = Math.max(1, Math.min(5, Math.round(rating)));
  const color = rarityColorFor(rating);
  return (
    <span
      className="inline-block whitespace-nowrap rounded px-2.5 py-[3px] text-[11px] font-black uppercase tracking-wider text-[var(--color-xk-navy)]"
      style={{ backgroundColor: color, fontFamily: 'var(--font-display)' }}
    >
      {t(`stock.card.rarityLabel.${tier}`)}
    </span>
  );
}

function RarityBar({ rating }: { rating: number }) {
  const t = useT();
  const tier = Math.max(1, Math.min(5, Math.round(rating)));
  const color = rarityColorFor(rating);
  const label = t(`stock.card.rarityLabel.${tier}`);
  return (
    <div className="flex flex-1 gap-1" aria-label={`${t('stock.card.rarityTier')} ${label} (${rating}/5)`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="h-1 flex-1 rounded-sm"
          style={{
            background: i <= rating ? color : 'rgb(255 255 255 / 0.10)',
          }}
        />
      ))}
    </div>
  );
}
